/**
 * `env` on every answer (surfaces.md §3.5): what her keyboard produces.
 *
 * Read once per sitting from `navigator.keyboard.getLayoutMap()` where the
 * browser offers it (Chromium on a desktop): what `Digit1`–`Digit0`,
 * `Semicolon`, `Slash`, `Period` and `Comma` produce. Where it is absent the
 * map is `null`, and the hint is read from the trace itself: a `Digit3` that
 * produced `ę` is the Windows Lithuanian layout.
 */
import type { Env, TraceEntry } from "./api";

const CODES = [
  "Digit1", "Digit2", "Digit3", "Digit4", "Digit5", "Digit6", "Digit7", "Digit8", "Digit9", "Digit0",
  "Semicolon", "Slash", "Period", "Comma",
];

const LT_DIGIT_ROW = "ąčęėįšųū";

export function hintFromMap(map: Record<string, string>): Env["layout_hint"] {
  const d1 = map.Digit1;
  if (d1 === undefined) return "unknown";
  if (LT_DIGIT_ROW.includes(d1)) return "lt";
  if (/^[0-9]$/.test(d1)) return map.Semicolon === ";" ? "us" : "other";
  // Lithuanian Standard: the unshifted digit row is punctuation.
  if (!/[a-z]/i.test(d1)) return "lt-std";
  return "other";
}

let cached: Pick<Env, "layout_hint" | "layout_basis" | "layout_map"> | null = null;

interface KeyboardApi {
  getLayoutMap(): Promise<Map<string, string>>;
}

export async function readLayout(): Promise<void> {
  const kb = (navigator as unknown as { keyboard?: KeyboardApi }).keyboard;
  if (!kb?.getLayoutMap) return;
  try {
    const m = await kb.getLayoutMap();
    const map: Record<string, string> = {};
    for (const c of CODES) {
      const v = m.get(c);
      if (v !== undefined) map[c] = v;
    }
    cached = { layout_hint: hintFromMap(map), layout_basis: "getLayoutMap", layout_map: map };
  } catch {
    cached = null;
  }
}

/** From the trace, when the browser has no layout map: the first digit key
 *  whose character was not its digit names the layout. */
export function hintFromTrace(trace: TraceEntry[]): Pick<Env, "layout_hint" | "layout_basis"> {
  for (const e of trace) {
    const code = e[1];
    const key = e[2];
    const m = /^Digit([0-9])$/.exec(code);
    if (!m) continue;
    if (key === m[1]) return { layout_hint: "unknown", layout_basis: `${code}→${key}` };
    if (LT_DIGIT_ROW.includes(key)) return { layout_hint: "lt", layout_basis: `${code}→${key}` };
    return { layout_hint: "other", layout_basis: `${code}→${key}` };
  }
  return { layout_hint: "unknown", layout_basis: "none" };
}

let numpadSeen: boolean | null = null;

export function noteKey(code: string): void {
  if (code.startsWith("Numpad")) numpadSeen = true;
}

export function envFor(trace: TraceEntry[]): Env {
  if (cached) return { ...cached, numpad: numpadSeen };
  return { ...hintFromTrace(trace), layout_map: null, numpad: numpadSeen };
}
